/**
 * Device Information Collection Utility
 * Collects device information from the browser for tracking purposes
 */

export interface DeviceInfo {
  userAgent: string;
  deviceType: 'mobile' | 'desktop' | 'tablet';
  operatingSystem: string;
  browser: string;
  screenResolution: string;
  timezone: string;
  language: string;
  metadata: Record<string, any>;
  deviceFingerprint: string;
}

/**
 * Generate a device fingerprint hash from user agent, screen resolution, and timezone
 */
function generateDeviceFingerprint(userAgent: string, screenResolution: string, timezone: string): string {
  const data = `${userAgent}|${screenResolution}|${timezone}`;
  
  // Simple hash function (for production, consider using crypto.subtle.digest)
  let hash = 0;
  for (let i = 0; i < data.length; i++) {
    const char = data.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  // Convert to positive hex string
  return Math.abs(hash).toString(16);
}

/**
 * Parse operating system from user agent string
 */
function parseOperatingSystem(userAgent: string): string {
  const ua = userAgent.toLowerCase();
  
  if (ua.includes('windows')) {
    if (ua.includes('windows nt 10')) return 'Windows 10/11';
    if (ua.includes('windows nt 6.3')) return 'Windows 8.1';
    if (ua.includes('windows nt 6.2')) return 'Windows 8';
    if (ua.includes('windows nt 6.1')) return 'Windows 7';
    return 'Windows';
  }
  if (ua.includes('mac os x') || ua.includes('macintosh')) {
    const match = ua.match(/mac os x (\d+)[._](\d+)/);
    if (match) {
      return `macOS ${match[1]}.${match[2]}`;
    }
    return 'macOS';
  }
  if (ua.includes('linux')) return 'Linux';
  if (ua.includes('android')) {
    const match = ua.match(/android (\d+\.\d+)/);
    if (match) {
      return `Android ${match[1]}`;
    }
    return 'Android';
  }
  if (ua.includes('iphone') || ua.includes('ipad')) {
    const match = ua.match(/os (\d+)[._](\d+)/);
    if (match) {
      return `iOS ${match[1]}.${match[2]}`;
    }
    return 'iOS';
  }
  
  return 'Unknown';
}

/**
 * Parse browser from user agent string
 */
function parseBrowser(userAgent: string): string {
  const ua = userAgent.toLowerCase();
  
  if (ua.includes('edg/')) {
    const match = ua.match(/edg\/(\d+)/);
    return match ? `Edge ${match[1]}` : 'Edge';
  }
  if (ua.includes('chrome/') && !ua.includes('edg/')) {
    const match = ua.match(/chrome\/(\d+)/);
    return match ? `Chrome ${match[1]}` : 'Chrome';
  }
  if (ua.includes('firefox/')) {
    const match = ua.match(/firefox\/(\d+)/);
    return match ? `Firefox ${match[1]}` : 'Firefox';
  }
  if (ua.includes('safari/') && !ua.includes('chrome/')) {
    const match = ua.match(/version\/(\d+)/);
    return match ? `Safari ${match[1]}` : 'Safari';
  }
  if (ua.includes('opera/') || ua.includes('opr/')) {
    const match = ua.match(/(?:opera|opr)\/(\d+)/);
    return match ? `Opera ${match[1]}` : 'Opera';
  }
  
  return 'Unknown';
}

/**
 * Detect device type from user agent and screen size
 */
function detectDeviceType(userAgent: string): 'mobile' | 'desktop' | 'tablet' {
  const ua = userAgent.toLowerCase();
  const width = typeof window !== 'undefined' ? window.screen.width : 0;
  
  // Check for mobile devices
  if (ua.includes('mobile') && !ua.includes('tablet')) {
    return 'mobile';
  }
  
  // Check for tablets
  if (ua.includes('tablet') || ua.includes('ipad') || (ua.includes('android') && !ua.includes('mobile'))) {
    return 'tablet';
  }
  
  // Check screen size as fallback
  if (width > 0 && width < 768) {
    return 'mobile';
  }
  if (width >= 768 && width < 1024) {
    return 'tablet';
  }
  
  return 'desktop';
}

/**
 * Collect device information from the browser
 */
export function collectDeviceInfo(): DeviceInfo {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    throw new Error('Device info collection requires browser environment');
  }

  const userAgent = navigator.userAgent;
  const screenWidth = window.screen.width;
  const screenHeight = window.screen.height;
  const screenResolution = `${screenWidth}x${screenHeight}`;
  
  // Get timezone
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  // Get language
  const language = navigator.language || 'en';
  
  // Parse OS and browser
  const operatingSystem = parseOperatingSystem(userAgent);
  const browser = parseBrowser(userAgent);
  
  // Detect device type
  const deviceType = detectDeviceType(userAgent);
  
  // Generate device fingerprint
  const deviceFingerprint = generateDeviceFingerprint(userAgent, screenResolution, timezone);
  
  // Collect metadata
  const metadata: Record<string, any> = {};
  
  // Hardware concurrency (number of CPU cores)
  if ('hardwareConcurrency' in navigator) {
    metadata.hardwareConcurrency = (navigator as any).hardwareConcurrency;
  }
  
  // Device memory (if available)
  if ('deviceMemory' in navigator) {
    metadata.deviceMemory = (navigator as any).deviceMemory;
  }
  
  // Connection type (if available)
  if ('connection' in navigator) {
    const conn = (navigator as any).connection;
    if (conn) {
      metadata.connection = {
        effectiveType: conn.effectiveType,
        downlink: conn.downlink,
        rtt: conn.rtt,
        saveData: conn.saveData,
      };
    }
  }
  
  // Platform
  if ('platform' in navigator) {
    metadata.platform = navigator.platform;
  }
  
  // Cookie enabled
  metadata.cookieEnabled = navigator.cookieEnabled;
  
  // Do not track
  metadata.doNotTrack = navigator.doNotTrack || null;
  
  return {
    userAgent,
    deviceType,
    operatingSystem,
    browser,
    screenResolution,
    timezone,
    language,
    metadata,
    deviceFingerprint,
  };
}

